// @ts-nocheck
import { generateClient } from "aws-amplify/api";
import { uploadData } from "@aws-amplify/storage";
import { processedModelsByAssetIdCustom } from "../../graphql/customQueries";
import { createProcessedModelCustom, updateProcessedModelCustom } from "../../graphql/customMutations";
import { processedSample } from "../../assets/json/processedSample";

const client = generateClient();

export interface AdditionalDataPoint {
  id: string;
  name: string;
  parameterId?: number;
  source?: string;
  schema?: string;
  lookbackDays?: number;
  color?: string;
}

export interface ProcessedModelRecord {
  id: string;
  assetId: string;
  label: string;
  description?: string;
  routeShort: string;
  routeLong: string;
  displayType: string;
  modelType: string;
  owner: string;
  organizationId?: string;
  additionalDataPoints?: AdditionalDataPoint[];
  createdAt: string;
  updatedAt: string;
}

export interface Asset {
  id: string;
  name: string;
  owner: string;
  organizationId?: string;
  assetGroupId: string;
}

export interface AssetGroup {
  id: string;
  name: string;
  organizationId?: string;
}

/**
 * Fetch all ProcessedModels for a list of assets
 */
export const fetchProcessedModelsForGroup = async (
  assetIds: string[]
): Promise<ProcessedModelRecord[]> => {
  try {
    const allModels: ProcessedModelRecord[] = [];

    // Fetch ProcessedModels for each asset
    for (const assetId of assetIds) {
      try {
        const { data } = await client.graphql({
          query: processedModelsByAssetIdCustom,
          variables: { assetId },
        });

        if (data?.processedModelsByAssetId?.items) {
          allModels.push(...data.processedModelsByAssetId.items);
        }
      } catch (error) {
        console.error(`Error fetching ProcessedModels for asset ${assetId}:`, error);
      }
    }

    return allModels;
  } catch (error) {
    console.error("Error fetching ProcessedModels for group:", error);
    throw error;
  }
};

/**
 * Generate S3 path based on ownership and organization
 */
export const generateS3Path = (
  asset: Asset,
  customProcessedModelShort: string,
  cognitoIdentityId: string,
  organizationId?: string
): string => {
  if (organizationId) {
    return `organizationprivate/${organizationId}/assets/${asset.id}/processedModels/${customProcessedModelShort}/`;
  } else {
    return `private/${cognitoIdentityId}/assets/${asset.id}/processedModels/${customProcessedModelShort}/`;
  }
};

/**
 * Create a new ProcessedModel with placeholder file upload
 */
export const createProcessedModel = async (input: {
  assetId: string;
  label: string;
  description?: string;
  routeShort: string;
  owner: string;
  cognitoIdentityId: string;
  organizationId?: string;
  additionalDataPoints?: AdditionalDataPoint[];
}): Promise<ProcessedModelRecord> => {
  try {
    const routeLong = generateS3Path(
      { id: input.assetId } as Asset,
      input.routeShort,
      input.cognitoIdentityId,
      input.organizationId
    );

    const newProcessedModel = {
      assetId: input.assetId,
      label: input.label,
      description: input.description,
      routeShort: input.routeShort,
      routeLong,
      displayType: "scatter",
      modelType: "PROCESSED",
      owner: input.owner,
      organizationId: input.organizationId,
      additionalDataPoints: input.additionalDataPoints,
    };

    // Create the ProcessedModel record
    const { data } = await client.graphql({
      query: createProcessedModelCustom,
      variables: { input: newProcessedModel },
    });

    // Upload placeholder file to S3
    try {
      const jsonData = JSON.stringify(processedSample);
      await uploadData({
        path: `${routeLong}2024-08-20-06-placeholder.json`,
        data: new Blob([jsonData], { type: "application/json" }),
        options: {
          contentType: "application/json",
        },
      }).result;

      console.log("Placeholder file uploaded successfully");
    } catch (uploadError) {
      console.error("Error uploading placeholder file to S3:", uploadError);
      // Don't throw - the DB record was created successfully
    }

    return data.createProcessedModel;
  } catch (error) {
    console.error("Error creating ProcessedModel:", error);
    throw error;
  }
};

/**
 * Update an existing ProcessedModel
 */
export const updateProcessedModelRecord = async (input: {
  id: string;
  label?: string;
  description?: string;
  owner?: string;
  organizationId?: string;
  additionalDataPoints?: AdditionalDataPoint[];
}): Promise<ProcessedModelRecord> => {
  try {
    // Strip __typename from additionalDataPoints if present
    const cleanInput = {
      ...input,
      additionalDataPoints: input.additionalDataPoints?.map(({ __typename, ...rest }: any) => rest),
    };

    const { data } = await client.graphql({
      query: updateProcessedModelCustom,
      variables: { input: cleanInput },
    });

    return data.updateProcessedModel;
  } catch (error) {
    console.error("Error updating ProcessedModel:", error);
    throw error;
  }
};
